// Lingue, sezioni e indirizzi: modulo SENZA dizionari, così lo importa anche il Worker (pagine dei lanci, delle
// aziende e dei paesi, sitemap) senza portarsi dietro tutti i testi.

export type Lingua = 'en' | 'it' | 'fr' | 'de' | 'es';
export const LINGUE: Lingua[] = ['en', 'it', 'fr', 'de', 'es'];
export const LINGUA_PREDEFINITA: Lingua = 'en';

export type Sezione = 'esplora' | 'lanci' | 'classifica' | 'potenze' | 'storia' | 'eventi' | 'metodologia' | 'fonti' | 'dati' | 'stato' | 'privacy';

export const PERCORSI: Record<Sezione, Record<Lingua, string>> = {
  esplora: { en: '/', it: '/it', fr: '/fr', de: '/de', es: '/es' },
  lanci: { en: '/launches', it: '/it/lanci', fr: '/fr/lancements', de: '/de/starts', es: '/es/lanzamientos' },
  classifica: { en: '/ranking', it: '/it/classifica', fr: '/fr/classement', de: '/de/rangliste', es: '/es/clasificacion' },
  potenze: { en: '/space-powers', it: '/it/potenze-spaziali', fr: '/fr/puissances-spatiales', de: '/de/raumfahrtmaechte', es: '/es/potencias-espaciales' },
  storia: { en: '/history', it: '/it/storia', fr: '/fr/histoire', de: '/de/geschichte', es: '/es/historia' },
  eventi: { en: '/events', it: '/it/eventi', fr: '/fr/evenements', de: '/de/ereignisse', es: '/es/eventos' },
  metodologia: { en: '/methodology', it: '/it/metodologia', fr: '/fr/methodologie', de: '/de/methodik', es: '/es/metodologia' },
  fonti: { en: '/sources', it: '/it/fonti', fr: '/fr/sources', de: '/de/quellen', es: '/es/fuentes' },
  dati: { en: '/data', it: '/it/dati', fr: '/fr/donnees', de: '/de/daten', es: '/es/datos' },
  stato: { en: '/status', it: '/it/stato', fr: '/fr/etat', de: '/de/status', es: '/es/estado' },
  privacy: { en: '/privacy', it: '/it/privacy', fr: '/fr/confidentialite', de: '/de/datenschutz', es: '/es/privacidad' },
};

/** Sezioni con pagine di dettaglio sotto il loro indirizzo: /launches/<slug>, /ranking/<azienda>, /ranking/<paese>/<ISO>. */
export const CON_DETTAGLIO: Sezione[] = ['lanci', 'classifica'];
/** Pagine dei satelliti noti (dati/satelliti-noti.ts): /satellite/<slug>, dentro la sezione Esplora. */
export const PERCORSO_SATELLITE: Record<Lingua, string> = { en: '/satellite', it: '/it/satellite', fr: '/fr/satellite', de: '/de/satellit', es: '/es/satelite' };
/** Pagine dei razzi (dati `razzi.json`): /rockets/<slug>, dentro la sezione Lanci. */
export const PERCORSO_RAZZO: Record<Lingua, string> = { en: '/rockets', it: '/it/razzi', fr: '/fr/fusees', de: '/de/raketen', es: '/es/cohetes' };
/** Pagine delle basi di lancio (dati `basi.json`): /launch-sites/<slug>, dentro la sezione Lanci. */
export const PERCORSO_BASE: Record<Lingua, string> = { en: '/launch-sites', it: '/it/basi-di-lancio', fr: '/fr/bases-de-lancement', de: '/de/startplaetze', es: '/es/bases-de-lanzamiento' };
/** Segmento che precede il codice ISO di un paese nelle pagine della classifica. */
export const SEGMENTO_PAESE: Record<Lingua, string> = { en: 'country', it: 'paese', fr: 'pays', de: 'land', es: 'pais' };

export interface Risolto { lingua: Lingua; sezione: Sezione; /** parte dopo l'indirizzo della sezione (es. lo slug di un lancio), senza barra iniziale */ resto: string | null }

/** Da un percorso (es. «/it/lanci/falcon-9-…») a lingua, sezione e dettaglio; null se non è una pagina del sito. */
export function risolvi(percorso: string): Risolto | null {
  const p = percorso.replace(/\/+$/, '') || '/';
  for (const l of LINGUE) {
    const base = PERCORSO_SATELLITE[l] + '/';
    if (p.startsWith(base) && p.length > base.length) return { lingua: l, sezione: 'esplora', resto: `satellite/${p.slice(base.length)}` };
    const baseRazzo = PERCORSO_RAZZO[l] + '/';
    if (p.startsWith(baseRazzo) && p.length > baseRazzo.length) return { lingua: l, sezione: 'lanci', resto: `razzo/${p.slice(baseRazzo.length)}` };
    const baseBase = PERCORSO_BASE[l] + '/';
    if (p.startsWith(baseBase) && p.length > baseBase.length) return { lingua: l, sezione: 'lanci', resto: `base/${p.slice(baseBase.length)}` };
  }
  let migliore: Risolto | null = null;
  let lunghezza = -1;
  for (const [sezione, perLingua] of Object.entries(PERCORSI) as [Sezione, Record<Lingua, string>][]) {
    for (const l of LINGUE) {
      const base = perLingua[l];
      if (p === base) return { lingua: l, sezione, resto: null };
      if (CON_DETTAGLIO.includes(sezione) && p.startsWith(base + '/') && base.length > lunghezza) {
        migliore = { lingua: l, sezione, resto: p.slice(base.length + 1) };
        lunghezza = base.length;
      }
    }
  }
  return migliore;
}

/** Indirizzo della pagina di un satellite noto. */
export const indirizzoSatellite = (l: Lingua, slug: string) => `${PERCORSO_SATELLITE[l]}/${slug}`;
/** Lo stesso indirizzo (anche di dettaglio) in un'altra lingua: cambiando lingua non si perde ciò che si stava guardando. */
export function traduciPercorso(r: Risolto, l: Lingua): string {
  if (!r.resto) return PERCORSI[r.sezione][l];
  if (r.sezione === 'esplora' && r.resto.startsWith('satellite/')) return indirizzoSatellite(l, r.resto.slice('satellite/'.length));
  if (r.sezione === 'lanci') {
    if (r.resto.startsWith('razzo/')) return indirizzoRazzo(l, r.resto.slice('razzo/'.length));
    if (r.resto.startsWith('base/')) return indirizzoBase(l, r.resto.slice('base/'.length));
    return indirizzoLancio(l, r.resto);
  }
  if (r.sezione === 'classifica') {
    const seg = SEGMENTO_PAESE[r.lingua] + '/';
    return r.resto.startsWith(seg) ? indirizzoPaese(l, r.resto.slice(seg.length)) : indirizzoAzienda(l, r.resto);
  }
  return PERCORSI[r.sezione][l];
}
/** Indirizzo della pagina di un razzo. */
export const indirizzoRazzo = (l: Lingua, slug: string) => `${PERCORSO_RAZZO[l]}/${slug}`;
/** Indirizzo della pagina di una base di lancio. */
export const indirizzoBase = (l: Lingua, slug: string) => `${PERCORSO_BASE[l]}/${slug}`;
/** Indirizzo della pagina di un lancio. */
export const indirizzoLancio = (l: Lingua, slug: string) => `${PERCORSI.lanci[l]}/${slug}`;
/** Indirizzo della pagina di un'azienda dell'Indice (vettore o operatore). */
export const indirizzoAzienda = (l: Lingua, id: string) => `${PERCORSI.classifica[l]}/${id}`;
/** Indirizzo della pagina di un paese nell'Indice (codice ISO alpha-3). */
export const indirizzoPaese = (l: Lingua, a3: string) => `${PERCORSI.classifica[l]}/${SEGMENTO_PAESE[l]}/${a3.toLowerCase()}`;
